/**
 * The Family Studio's edit panels (#577 rows 7, 9 and 10).
 *
 * ONE panel at a time, rendered inside the page rather than as a modal, so there is no focus trap to
 * get wrong and the page around it stays readable. Each form is presentational: it reports what the
 * parent asked for through a callback, and the container (`FamilyRoute.jsx`) makes the request,
 * closes the panel on success and passes an error back when the server refuses.
 *
 * Two actions that cannot be taken back in one press — deactivating a member and deleting a goal —
 * ask a second time, naming exactly what will happen.
 *
 * There is deliberately no role control: the server refuses a role change by design
 * (`updateUserSchema` carries no `role`), so nothing here may imply one exists.
 */
import React, { useEffect, useId, useRef, useState } from 'react';

import Button from './Button.jsx';
import Field from './Field.jsx';
import { GOAL_TYPE_LABELS } from '../lib/familyMapping.js';

/** Goal types the server accepts (`goal.service.js`, `createGoal`), in the family's words. */
const GOAL_OPTIONS = Object.entries(GOAL_TYPE_LABELS).map(([value, label]) => ({ value, label }));
const isRewardGoal = (type) => String(type || '').endsWith('_rewards');
const targetLabelFor = (type) => (isRewardGoal(type) ? 'How much pocket money' : 'How many quests');

/** The shell every panel shares: a named region, its heading, an error line and Cancel. */
export function FamilyEditPanel({ title, error, busy, onCancel, children }) {
  const headingId = useId();
  const ref = useRef(null);
  // Move focus to the panel when it opens, so a keyboard user lands where the change happens.
  useEffect(() => { if (ref.current) ref.current.focus(); }, []);
  return (
    <section
      ref={ref}
      tabIndex={-1}
      role="region"
      aria-label={title}
      aria-busy={busy ? 'true' : undefined}
      className="fam-editor"
      data-part="editor"
    >
      <h2 className="fam-editor-title" id={headingId}>{title}</h2>
      <div className="fam-editor-body">{children}</div>
      {error ? <p className="fam-editor-error" role="alert">{error}</p> : null}
      <div className="fam-editor-actions">
        <Button variant="quiet" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
      </div>
    </section>
  );
}

function positiveNumber(text) {
  const n = Number(String(text).trim());
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Rename, deactivate or reactivate one member, and move them in the family order.
 *
 * `isSelf` hides Deactivate on the parent's own card; the server refuses it too
 * (`admin.routes.js`, "You cannot deactivate your own account").
 */
export function MemberEditor({
  member, isSelf = false, canMoveEarlier = false, canMoveLater = false,
  busy = false, error, onSave, onDeactivate, onReactivate, onMove, onCancel,
}) {
  const [name, setName] = useState(member?.displayName || member?.name || '');
  const [confirming, setConfirming] = useState(false);
  const shown = member?.name || 'this member';
  const trimmed = name.trim();

  return (
    <FamilyEditPanel title={`Edit ${shown}`} error={error} busy={busy} onCancel={onCancel}>
      <form
        className="fam-editor-row"
        onSubmit={(event) => { event.preventDefault(); if (trimmed) onSave(trimmed); }}
      >
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} disabled={busy} autoComplete="off" />
        <Button type="submit" variant="primary" size="sm" disabled={busy || !trimmed}>Save name</Button>
      </form>

      {member?.isActive ? (
        <div className="fam-editor-row">
          {canMoveEarlier ? <Button size="sm" variant="secondary" onClick={() => onMove('earlier')} disabled={busy}>Move earlier</Button> : null}
          {canMoveLater ? <Button size="sm" variant="secondary" onClick={() => onMove('later')} disabled={busy}>Move later</Button> : null}
        </div>
      ) : null}

      {member?.isActive && !isSelf ? (
        confirming ? (
          <div className="fam-editor-confirm">
            <p>{`${shown} will not be able to sign in until a parent reactivates them. Their history stays.`}</p>
            <div className="fam-editor-row">
              <Button size="sm" variant="danger" onClick={onDeactivate} disabled={busy}>{`Yes, deactivate ${shown}`}</Button>
              <Button size="sm" variant="quiet" onClick={() => setConfirming(false)} disabled={busy}>{`Keep ${shown} signed in`}</Button>
            </div>
          </div>
        ) : (
          <div className="fam-editor-row">
            <Button size="sm" variant="secondary" onClick={() => setConfirming(true)} disabled={busy}>Deactivate</Button>
          </div>
        )
      ) : null}

      {member && !member.isActive ? (
        <div className="fam-editor-row">
          <p>{`${shown} is deactivated and cannot sign in.`}</p>
          <Button size="sm" variant="primary" onClick={onReactivate} disabled={busy}>Reactivate</Button>
        </div>
      ) : null}
    </FamilyEditPanel>
  );
}

/**
 * Add, change or remove one child's goal.
 *
 * `row` is the goals-table row; `row.hasGoal` switches between add and edit. With no row (the
 * section's "+ Add goal") the panel asks whose goal it is, offering only the children.
 */
export function GoalEditor({ row, kids = [], busy = false, error, onSave, onDelete, onCancel }) {
  const editing = Boolean(row?.hasGoal);
  const [whose, setWhose] = useState(row ? String(row.id) : String(kids[0]?.id ?? ''));
  const [goalType, setGoalType] = useState(row?.goalType || GOAL_OPTIONS[0].value);
  const [target, setTarget] = useState(editing && row.target != null ? String(row.target) : '');
  const [confirming, setConfirming] = useState(false);
  const amount = positiveNumber(target);

  let title = 'Add a goal';
  if (editing) title = `Edit ${row.name || 'this'}'s goal`;
  else if (row) title = `Add a goal for ${row.name || 'this member'}`;

  const submit = (event) => {
    event.preventDefault();
    if (!amount || !whose) return;
    onSave({ userId: Number(whose), goalType, target: amount });
  };

  return (
    <FamilyEditPanel title={title} error={error} busy={busy} onCancel={onCancel}>
      <form className="fam-editor-grid" onSubmit={submit}>
        {!row ? (
          <Field
            label="Whose goal"
            value={whose}
            onChange={(e) => setWhose(e.target.value)}
            options={kids.map((k) => ({ value: String(k.id), label: k.name }))}
            disabled={busy}
          />
        ) : null}
        {editing ? (
          <p className="fam-editor-fact">{`Goal: ${row.goal}`}</p>
        ) : (
          <Field label="Goal" value={goalType} onChange={(e) => setGoalType(e.target.value)} options={GOAL_OPTIONS} disabled={busy} />
        )}
        <Field
          label={targetLabelFor(goalType)}
          type="number"
          inputMode="decimal"
          min="1"
          step="any"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          disabled={busy}
        />
        <div className="fam-editor-row">
          <Button type="submit" variant="primary" size="sm" disabled={busy || !amount || !whose}>Save goal</Button>
        </div>
      </form>

      {editing ? (
        confirming ? (
          <div className="fam-editor-confirm">
            <p>{`This removes ${row.name || 'this'}'s goal and its progress bar. It cannot be undone.`}</p>
            <div className="fam-editor-row">
              <Button size="sm" variant="danger" onClick={onDelete} disabled={busy}>Yes, delete this goal</Button>
              <Button size="sm" variant="quiet" onClick={() => setConfirming(false)} disabled={busy}>Keep this goal</Button>
            </div>
          </div>
        ) : (
          <div className="fam-editor-row">
            <Button size="sm" variant="secondary" onClick={() => setConfirming(true)} disabled={busy}>Delete goal</Button>
          </div>
        )
      ) : null}
    </FamilyEditPanel>
  );
}

/** Set or change the household goal (`PUT /api/village`), which starts the village over. */
export function HouseholdGoalEditor({ goal, busy = false, error, onSave, onCancel }) {
  const [name, setName] = useState(goal?.name || '');
  const [points, setPoints] = useState(goal?.pointTarget != null ? String(goal.pointTarget) : '');
  const target = positiveNumber(points);
  const whole = target !== null && Number.isInteger(target);
  const trimmed = name.trim();

  return (
    <FamilyEditPanel
      title={goal ? 'Change the household goal' : 'Set a household goal'}
      error={error}
      busy={busy}
      onCancel={onCancel}
    >
      <p className="fam-editor-fact">
        When you set a new goal, the village starts over from now. Points the family earned toward the
        old goal do not carry across.
      </p>
      <form
        className="fam-editor-grid"
        onSubmit={(event) => { event.preventDefault(); if (trimmed && whole) onSave({ name: trimmed, pointTarget: target }); }}
      >
        <Field label="Goal name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} disabled={busy} />
        <Field label="Points to reach" type="number" inputMode="numeric" min="1" step="1" value={points} onChange={(e) => setPoints(e.target.value)} disabled={busy} />
        <div className="fam-editor-row">
          <Button type="submit" variant="primary" size="sm" disabled={busy || !trimmed || !whole}>Save household goal</Button>
        </div>
      </form>
    </FamilyEditPanel>
  );
}

/** Rename one paired display (E38). */
export function DeviceRenameEditor({ device, busy = false, error, onSave, onCancel }) {
  const [name, setName] = useState(device?.name || '');
  const trimmed = name.trim();
  return (
    <FamilyEditPanel title={`Rename ${device?.name || 'this display'}`} error={error} busy={busy} onCancel={onCancel}>
      <form className="fam-editor-row" onSubmit={(event) => { event.preventDefault(); if (trimmed) onSave(trimmed); }}>
        <Field label="Display name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} disabled={busy} autoComplete="off" />
        <Button type="submit" variant="primary" size="sm" disabled={busy || !trimmed}>Save display name</Button>
      </form>
    </FamilyEditPanel>
  );
}
